const defaultFont = `font-family: 'Pretendard'; font-style: normal;`;

type TitleTypo = "t1" | "t2" | "t3";
const getTitleTypo = (size: TitleTypo): string => {
  const fontWeight = "700";
  let fontSize;
  let lineHeight;
  switch (size) {
    case "t1":
      fontSize = "48px";
      lineHeight = "58px";
      break;
    case "t2":
      fontSize = "40px";
      lineHeight = "52px";
      break;
    case "t3":
      fontSize = "32px";
      lineHeight = "48px";
      break;
    default:
      break;
  }
  return `${defaultFont} font-weight: ${fontWeight}; font-size: ${fontSize}; line-height: ${lineHeight}`;
};

type HeadingTypo = "b1" | "b2" | "b3" | "b4" | "b5";
const getHeadingTypo = (size: HeadingTypo) => {
  const fontWeight = "700";
  let fontSize;
  let lineHeight;
  switch (size) {
    case "b1":
      fontSize = "24px";
      lineHeight = "32px";
      break;
    case "b2":
      fontSize = "20px";
      lineHeight = "32px";
      break;
    case "b3":
      fontSize = "18px";
      lineHeight = "28px";
      break;
    case "b4":
      fontSize = "16px";
      lineHeight = "24px";
      break;
    case "b5":
      fontSize = "14px";
      lineHeight = "24px";
      break;
    default:
      break;
  }
  return `${defaultFont} font-weight: ${fontWeight}; font-size: ${fontSize}; line-height: ${lineHeight}`;
};

type BodyTypo = "r1" | "r2" | "r3" | "r4" | "m1" | "m2" | "m3" | "m4";
const getBodyTypo = (size: BodyTypo) => {
  let fontWeight;
  let fontSize;
  let lineHeight;
  switch (size) {
    case "r1":
      fontWeight = "400";
      fontSize = "18px";
      lineHeight = "24px";
      break;
    case "r2":
      fontWeight = "400";
      fontSize = "16px";
      lineHeight = "24px";
      break;
    case "r3":
      fontWeight = "400";
      fontSize = "14px";
      lineHeight = "24px";
      break;
    case "r4":
      fontWeight = "400";
      fontSize = "12px";
      lineHeight = "18px";
      break;
    case "m1":
      fontWeight = "500";
      fontSize = "18px";
      lineHeight = "24px";
      break;
    case "m2":
      fontWeight = "500";
      fontSize = "16px";
      lineHeight = "24px";
      break;
    case "m3":
      fontWeight = "500";
      fontSize = "14px";
      lineHeight = "24px";
      break;
    case "m4":
      fontWeight = "500";
      fontSize = "12px";
      lineHeight = "18px";
      break;
    default:
      break;
  }
  return `${defaultFont} font-weight: ${fontWeight}; font-size: ${fontSize}; line-height: ${lineHeight}`;
};

export type ButtonTypo = "m1" | "m2" | "m3" | "m4";
const getButtonTypo = (size: ButtonTypo) => {
  const fontWeight = "500";
  let fontSize;
  let lineHeight;
  switch (size) {
    case "m1":
      fontSize = "18px";
      lineHeight = "22px";
      break;
    case "m2":
      fontSize = "16px";
      lineHeight = "20px";
      break;
    case "m3":
      fontSize = "14px";
      lineHeight = "16px";
      break;
    case "m4":
      fontSize = "12px";
      lineHeight = "14px";
      break;
    default:
      break;
  }
  return `${defaultFont} font-weight: ${fontWeight}; font-size: ${fontSize}; line-height: ${lineHeight}`;
};

type ShadowType = "1DP" | "2DP" | "4DP" | "8DP" | "16DP";
const getShadowScale = (size: ShadowType) => {
  let style = {
    x: 0,
    y: 0,
    blur: 1,
    opacity: 0,
  };

  switch (size) {
    case "1DP":
      style = {
        x: 0,
        y: 1,
        blur: 1,
        opacity: 5,
      };
      break;
    case "2DP":
      style = {
        x: 0,
        y: 2,
        blur: 10,
        opacity: 10,
      };
      break;
    case "4DP":
      style = {
        x: 0,
        y: 4,
        blur: 20,
        opacity: 15,
      };
      break;
    case "8DP":
      style = {
        x: 4,
        y: 4,
        blur: 40,
        opacity: 15,
      };
      break;
    case "16DP":
      style = {
        x: 0,
        y: 8,
        blur: 40,
        opacity: 20,
      };
      break;
    default:
      break;
  }

  return `box-shadow: ${style.x}px ${style.y}px ${
    style.blur
  }px rgba(25, 25, 30, ${style.opacity / 100})`;
};

export type Theme = typeof theme;
export const theme = {
  colors: {
    mainPrimary1: "#380A8A",
    mainPrimary2: "#48189E",
    /**
     * @color default
     */
    mainPrimary3: "#7B3FF1",
    mainPrimary4: "#9564F4",
    mainPrimary5: "#B08CF7",
    /**
     * @color hover
     */
    mainPrimary6: "#CAB2F9",
    mainPrimary7: "#E5D9FC",
    mainPrimary8: "#F2EBFE",
    mainPrimary9: "#F7F3FF",

    black: "#000000",
    /**
     * @color text
     */
    gray1: "#242426",
    gray2: "#464646",
    gray3: "#6E6E6E",
    gray4: "#8C8C8C",
    gray5: "#AAAAAA",
    /**
     * @color input border
     */
    gray6: "#CDCDCD",
    /**
     * @color border
     */
    gray7: "#DCDCDC",
    gray8: "#EFEFEF",
    gray9: "#F8F8F8",
    white: "#FFFFFF",

    red1: "#BE0000",
    red2: "#D20000",
    /**
     * @color default
     */
    red3: "#E60000",
    red4: "#FF6464",
    red5: "#FEE5E6",
    red6: "#FFF8F7",

    pink1: "#D7052D",
    /**
     * @color default
     */
    pink2: "#FF2D55",
    pink3: "#FF87AF",
    pink4: "#FFB9E1",
    pink5: "#FFEBFF",

    orange1: "#FF9500",
    /**
     * @color default
     */
    orange2: "#FFA905",
    orange3: "#FFBF66",
    orange4: "#FFDFB3",
    orange5: "#FFF4E5",

    yellow1: "#E1AE00",
    /**
     * @color default
     */
    yellow2: "#FFCC00",
    yellow3: "#FFE066",
    yellow4: "#FFF0B3",
    yellow5: "#FFF9E2",

    green1: "#006E1D",
    /**
     * @color default
     */
    green2: "#009645",
    green3: "#86DF9C",
    green4: "#C2EFCE",
    green5: "#E4FBEA",

    teal1: "#3CAADC",
    /**
     * @color default
     */
    teal2: "#5AC8FA",
    teal3: "#93D8FF",
    /**
     * @name Teal4
     */
    teal4: "#CDEEFE",
    /**
     * @name Teal5
     */
    teal5: "#E6F7FE",

    blue1: "#005CE1",
    /**
     * @color default
     */
    blue2: "#007AFF",
    blue3: "#52A5FF",
    blue4: "#B3D7FF",
    blue5: "#E5F2FF",

    mint1: "#284A5E",
    mint2: "#3B8C92",
    /**
     * @default
     */
    mint3: "#78BEC2",
    mint4: "#BADEE0",
    mint5: "#E8F9FA",
    shadow: "#19191E",
  },
  typo: {
    title: {
      t48: getTitleTypo("t1"),
      t40: getTitleTypo("t2"),
      t32: getTitleTypo("t3"),
    },
    heading: {
      b24: getHeadingTypo("b1"),
      b20: getHeadingTypo("b2"),
      b18: getHeadingTypo("b3"),
      b16: getHeadingTypo("b4"),
      b14: getHeadingTypo("b5"),
    },
    body: {
      r18: getBodyTypo("r1"),
      r16: getBodyTypo("r2"),
      r14: getBodyTypo("r3"),
      r12: getBodyTypo("r4"),
      m18: getBodyTypo("m1"),
      m16: getBodyTypo("m2"),
      m14: getBodyTypo("m3"),
      m12: getBodyTypo("m4"),
    },
    button: {
      m20: getButtonTypo("m1"),
      m16: getButtonTypo("m2"),
      m14: getButtonTypo("m3"),
      m12: getButtonTypo("m4"),
    },
  },
  shadow: {
    dp1: getShadowScale("1DP"),
    dp2: getShadowScale("2DP"),
    dp4: getShadowScale("4DP"),
    dp8: getShadowScale("8DP"),
    dp16: getShadowScale("16DP"),
  },
};
